/**
 * Mock-interview coaching-offer moments.
 *
 * Surfaces the free `generateMockInterview` server action at the two
 * highest-intent seeker moments: (a) a tracked application flips to
 * `interview` status, and (b) right after the seeker confirms they applied.
 * Both are genuine "you're about to need this" moments — unlike the Hunt
 * chat's Pro-upgrade prompts (`upgrade-moments.ts`), this offer is free and
 * has no plan gate; the only throttle is a soft per-month frequency cap so
 * an active user isn't nagged on every single application.
 *
 * This module is pure (no I/O, no flag reads) so the decision + copy are
 * unit-testable; the consuming component/action does the flag check,
 * session-dedupe, and telemetry around it.
 */

/** After this many coaching offers already shown this month, stay silent —
 * the seeker has clearly seen the surface; repeating it just nags. */
export const MAX_COACHING_OFFERS_PER_MONTH = 3;

export type CoachingMomentKind = "tracker_interview" | "post_apply";

export interface CoachingMomentInput {
	/** Current status of the application card, when the trigger is the tracker. */
	applicationStatus?: string;
	/** True when the trigger is a just-confirmed "mark as applied" action. */
	justApplied?: boolean;
	/** Whether the seeker has a resume on file — personalizes the copy, never gates it. */
	hasResume: boolean;
	/** Coaching offers (mock interview / CV deep-dive) already shown this month. */
	sessionsThisMonth: number;
}

export interface CoachingMomentCopy {
	title: string;
	body: string;
	cta: string;
}

export interface CoachingMoment {
	kind: CoachingMomentKind;
	copy: {
		en: CoachingMomentCopy;
		id: CoachingMomentCopy;
	};
}

/**
 * Decide which coaching moment (if any) warrants an offer. Returns `null`
 * when no offer should show. Only one moment fires per call so a caller
 * never has to reconcile two competing prompts.
 *
 * Priority: an interview-status tracker card outranks a fresh apply — the
 * seeker needs interview prep more urgently than post-apply filler.
 */
export function decideCoachingMoment(
	input: CoachingMomentInput,
): CoachingMoment | null {
	if (input.sessionsThisMonth >= MAX_COACHING_OFFERS_PER_MONTH) return null;

	if (input.applicationStatus === "interview") {
		return buildMoment("tracker_interview", input.hasResume);
	}
	if (input.justApplied) {
		return buildMoment("post_apply", input.hasResume);
	}
	return null;
}

function buildMoment(
	kind: CoachingMomentKind,
	hasResume: boolean,
): CoachingMoment {
	return { kind, copy: COPY[kind][hasResume ? "withResume" : "noResume"] };
}

const COPY: Record<
	CoachingMomentKind,
	{ withResume: CoachingMoment["copy"]; noResume: CoachingMoment["copy"] }
> = {
	tracker_interview: {
		withResume: {
			en: {
				title: "Got an interview? Practice it first",
				body: "We'll run a mock interview tailored to your resume and this role — the questions you'll actually get asked, with how to nail each one.",
				cta: "Practice for this interview",
			},
			id: {
				title: "Dapat interview? Latihan dulu",
				body: "Kami buatkan simulasi wawancara sesuai resume dan posisi ini — pertanyaan yang benar-benar akan ditanya, plus cara menjawabnya.",
				cta: "Latihan interview untuk posisi ini",
			},
		},
		noResume: {
			en: {
				title: "Got an interview? Practice it first",
				body: "We'll run a mock interview tailored to this role — add a resume anytime for even sharper questions, or start now.",
				cta: "Practice for this interview",
			},
			id: {
				title: "Dapat interview? Latihan dulu",
				body: "Kami buatkan simulasi wawancara sesuai posisi ini — unggah resume kapan saja untuk pertanyaan yang lebih tajam, atau mulai sekarang.",
				cta: "Latihan interview untuk posisi ini",
			},
		},
	},
	post_apply: {
		withResume: {
			en: {
				title: "Nice, application sent",
				body: "While you wait, run a quick mock interview tailored to your resume so you're ready the moment they call.",
				cta: "Practice for your next interview",
			},
			id: {
				title: "Lamaran terkirim",
				body: "Sambil menunggu, coba simulasi wawancara singkat sesuai resume kamu biar siap begitu mereka menghubungi.",
				cta: "Latihan untuk interview berikutnya",
			},
		},
		noResume: {
			en: {
				title: "Nice, application sent",
				body: "While you wait, run a quick mock interview so you're ready the moment they call — add a resume anytime for sharper questions.",
				cta: "Practice for your next interview",
			},
			id: {
				title: "Lamaran terkirim",
				body: "Sambil menunggu, coba simulasi wawancara singkat biar siap begitu mereka menghubungi — unggah resume kapan saja untuk pertanyaan lebih tajam.",
				cta: "Latihan untuk interview berikutnya",
			},
		},
	},
};
